import type { FC } from 'react';
import { useEffect } from 'react';
import { toast } from 'sonner';
import { v4 as uuid } from 'uuid';
import { useStorageLocationCreate } from 'src/apis/storage/use-create-storage-location';
import type { PublicMarketAreasRequest } from 'src/apis/storage/use-public-market-areas';
import type { PublicStorageLocationsRequest } from 'src/apis/storage/use-public-storage-locations';
import { Button } from 'src/components/buttons-and-actions/button';
import { Card } from 'src/components/data-display/card';
import { Form } from 'src/components/form/form';
import { PageHeader } from 'src/components/layout/page-header';
import type { Successful } from 'src/hooks/use-axios';
import { useReferrer } from 'src/hooks/use-referrer';
import { useTitle } from 'src/hooks/use-title';
import { CreateStorageLocationFormFields } from 'src/pages/storage/locations/create/fields';
import {
  formValuesToCreateRequest,
  getCreateStorageLocationSchema,
} from 'src/pages/storage/locations/create/schema';
import { Forbidden } from 'src/utils/errors';

type Props = {
  storageLocations: Successful<PublicStorageLocationsRequest>;
  marketAreas: Successful<PublicMarketAreasRequest>;
};

export const StorageLocationCreateForm: FC<Props> = ({
  storageLocations,
  marketAreas,
}) => {
  const title = 'Create Storage Location';
  useTitle(title);

  const referrer = useReferrer({
    label: 'Storage Locations',
    location: '/storage/locations',
  });

  const createStorageLocation = useStorageLocationCreate();

  useEffect(() => {
    if (!createStorageLocation.response) return;
    toast.success('Storage location was successfully created.');
    referrer.goTo();
    // oxlint-disable-next-line exhaustive-deps
  }, [createStorageLocation.response]);

  const marketAreasList = marketAreas.response.data._embedded.items;

  const createStorageLocationLink =
    storageLocations.response.data._links.createStorageLocation;

  if (!createStorageLocationLink) {
    throw new Forbidden();
  }

  return (
    <div
      className={`
        grid items-center gap-xs
        legacy:gap-[1rem]
      `}
    >
      <PageHeader title={title} referrer={referrer} />

      <Card data-testid="create-storage-location-form">
        <Form
          initialValues={{
            locationName: null,
            locationEic: null,
            marketAreas: [
              {
                id: uuid(),
                marketAreaId: null,
                injectionPoints: [],
                withdrawalPoints: [],
              },
            ],
            virtualPoint: 'false',
            comment: null,
            link: null,
          }}
          schema={getCreateStorageLocationSchema}
          onSubmit={(values) => {
            const data = formValuesToCreateRequest(values);
            createStorageLocation.execute({
              url: createStorageLocationLink.href,
              data,
            });
          }}
          constraintViolation={createStorageLocation.error}
          cancelOrResetButton={referrer}
          submitButton={
            <Button
              type="submit"
              disabled={createStorageLocation.pending}
              data-testid="save-storage-location"
            >
              {createStorageLocation.pending ? 'Pending...' : 'Save Location'}
            </Button>
          }
        >
          <CreateStorageLocationFormFields marketAreas={marketAreasList} />
        </Form>
      </Card>
    </div>
  );
};
